import { BookingChainService } from '@services/api/booking/booking-chain.service';
import { IBookingModel } from '@services/api/booking/booking-model.interface';
import { ModelLocalizationService } from '@services/formatter/model-localization.service';
import { BookingStatusService } from '@services/util/booking-status.service';
import { FlatInfoService } from '@services/util/flat-info.service';
import { toBookingRemarks } from '@services/util/booking-remarks';
import { GuestInfoService } from '@services/util/guest-info.service';
import { inject, Injectable } from '@angular/core';
import { ArrivalBookingRow, ConfirmableBookingRow, DepartureBookingRow, GuestBookingRow } from '../models/booking-overview.model';

/**
 * Service for mapping API booking models to view models
 */
@Injectable({
  providedIn: 'root'
})
export class BookingMapperService {
  private readonly guestInfoService = inject(GuestInfoService);
  private readonly flatInfoService = inject(FlatInfoService);
  private readonly localizer = inject(ModelLocalizationService);
  private readonly bookingStatusService = inject(BookingStatusService);
  private readonly chainService = inject(BookingChainService);

  /**
   * Map booking model to confirmable booking row for UI
   * @param booking Raw booking model from API
   * @param dataSource Data source for guest info
   * @returns Mapped ConfirmableBookingRow object
   */
  mapToConfirmableBookingRow(booking: IBookingModel): ConfirmableBookingRow {
    return {
      id: booking.id,
      guest: this.guestInfoService.createGuestInfo(booking),
      flatInfos: this.flatInfoService.extractFlatInfos(booking),
      checkInDate: booking.start_date || '',
      checkOutDate: booking.end_date || '',
      bookingStatus: {
        id: booking.bookingStatus?.id || 0,
        text: this.localizer.getBookingStatusKey(booking.bookingStatus || null),
        colorClasses: this.bookingStatusService.getStatusColorClasses(booking.bookingStatus || null),
        iconClasses: this.bookingStatusService.getStatusIconClasses(booking.bookingStatus || null)
      },
      isPaid: !!booking.paid,
      isCheckedIn: !!booking.checked_in,
      chain: this.chainService.marksFor(booking),
      remarks: toBookingRemarks(booking)
    };
  }

  /**
   * Map booking model to arrival booking row for UI
   * @param booking Raw booking model from API
   * @param dataSource Data source for guest info
   * @returns Mapped ArrivalBookingRow object
   */
  mapToArrivalBookingRow(booking: IBookingModel): ArrivalBookingRow {
    return {
      id: booking.id,
      reservationCode: booking.reservation_code,
      guest: this.guestInfoService.createGuestInfo(booking),
      flatInfos: this.flatInfoService.extractFlatInfos(booking),
      entryCodes: [], // Will be populated when loading entry codes
      entryCodesLoading: true,
      checkInDate: booking.start_date || '',
      checkOutDate: booking.end_date || '',
      isCheckedIn: booking.checked_in || false,
      hasPresenceWarning: false,
      presenceWarningLoading: true,
      chain: this.chainService.marksFor(booking),
      remarks: toBookingRemarks(booking)
    };
  }

  /**
   * Map booking model to guest booking row for UI
   * @param booking Raw booking model from API
   * @param dataSource Data source for guest info
   * @returns Mapped GuestBookingRow object
   */
  mapToGuestBookingRow(booking: IBookingModel): GuestBookingRow {
    return {
      id: booking.id,
      guest: this.guestInfoService.createGuestInfo(booking),
      flatInfos: this.flatInfoService.extractFlatInfos(booking),
      entryCodes: [], // Will be populated when loading entry codes
      entryCodesLoading: true,
      checkInDate: booking.start_date || '',
      checkOutDate: booking.end_date || '',
      isCheckedIn: booking.checked_in || false,
      chain: this.chainService.marksFor(booking),
      remarks: toBookingRemarks(booking)
    };
  }

  /**
   * Map booking model to departure booking row for UI
   * @param booking Raw booking model from API
   * @param dataSource Data source for guest info
   * @returns Mapped DepartureBookingRow object
   */
  mapToDepartureBookingRow(booking: IBookingModel): DepartureBookingRow {
    return {
      id: booking.id,
      guest: this.guestInfoService.createGuestInfo(booking),
      flatInfos: this.flatInfoService.extractFlatInfos(booking),
      entryCodes: [], // Will be populated when loading entry codes
      entryCodesLoading: true,
      checkInDate: booking.start_date || '',
      checkOutDate: booking.end_date || '',
      isCheckedIn: booking.checked_in || false,
      checkOutDateTime: booking.checkout_time || '',
      chain: this.chainService.marksFor(booking),
      remarks: toBookingRemarks(booking)
    };
  }
}
